import { arcPosition, lerp, randomBetween } from './math';
import type { Actor, Point, Theme } from './types';

interface Particle extends Point {
  vx: number;
  vy: number;
  age: number;
  life: number;
  radius: number;
  kind: 'smoke' | 'spark' | 'leaf' | 'crumb' | 'feather' | 'sparkle';
  rotation: number;
}
interface Projectile {
  from: Point;
  to: Point;
  age: number;
  duration: number;
  impact: () => void;
  radius: number;
  rotation: number;
}
interface Ring extends Point {
  age: number;
  life: number;
  radius: number;
  color: string;
}
export interface Summon extends Point {
  age: number;
  life: number;
  size: number;
}
export interface LogProp extends Summon {
  surface?: HTMLElement;
  offsetX: number;
}
export interface SpriteProp extends Point {
  asset: string;
  frame: number;
  size: number;
  rotation: number;
  opacity: number;
  age: number;
  life: number;
}

export class Effects {
  constructor(private drawProjectile: Theme['drawProjectile']) {}
  private particles: Particle[] = [];
  private projectiles: Projectile[] = [];
  private rings: Ring[] = [];
  summons: Summon[] = [];
  logs: LogProp[] = [];
  props: SpriteProp[] = [];
  afterimages: { actor: Actor; age: number; life: number }[] = [];
  charge: (Point & { radius: number; time: number; formation: number }) | undefined;

  smoke(x: number, y: number, scale = 1): void {
    for (let i = 0; i < 22; i++) {
      const angle = randomBetween(0, Math.PI * 2);
      const speed = randomBetween(28, 100) * scale;
      this.particles.push({
        x: x + randomBetween(-15, 15) * scale,
        y: y - randomBetween(0, 50) * scale,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 28,
        age: 0,
        life: randomBetween(0.5, 1.05),
        radius: randomBetween(10, 24) * scale,
        kind: 'smoke',
        rotation: angle,
      });
    }
    this.rings.push({
      x,
      y: y - 25 * scale,
      age: 0,
      life: 0.4,
      radius: 65 * scale,
      color: 'rgba(255,255,255,',
    });
  }

  dust(x: number, y: number): void {
    if (this.particles.length > 160) return;
    this.particles.push({
      x,
      y,
      vx: randomBetween(-20, 20),
      vy: -14,
      age: 0,
      life: 0.35,
      radius: 4,
      kind: 'smoke',
      rotation: 0,
    });
  }

  launch(
    from: Point,
    to: Point,
    impact: () => void,
    visual: { radius: number; rotation: number },
  ): void {
    this.projectiles.push({ from, to, age: 0, duration: 0.8, impact, ...visual });
  }

  burst(x: number, y: number): void {
    this.rings.push({ x, y, age: 0, life: 0.65, radius: 110, color: 'rgba(76,205,255,' });
    for (let i = 0; i < 40; i++) {
      const angle = randomBetween(0, Math.PI * 2);
      const speed = randomBetween(80, 240);
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        age: 0,
        life: randomBetween(0.3, 0.85),
        radius: randomBetween(1, 4),
        kind: 'spark',
        rotation: angle,
      });
    }
  }

  summon(x: number, y: number, size: number): void {
    this.smoke(x, y, 1.4);
    this.summons.push({ x, y, size, age: 0, life: 8 });
  }

  afterimage(actor: Actor): void {
    this.afterimages.push({ actor: { ...actor, motion: undefined }, age: 0, life: 0.22 });
  }

  flecks(x: number, y: number, kind: 'crumb' | 'feather', count = 6): void {
    for (let i = 0; i < count; i++)
      this.particles.push({
        x,
        y,
        vx: randomBetween(-38, 38),
        vy: randomBetween(-45, -12),
        age: 0,
        life: kind === 'feather' ? 1.1 : 0.6,
        radius: kind === 'feather' ? randomBetween(2, 4) : randomBetween(1, 2),
        rotation: randomBetween(-Math.PI, Math.PI),
        kind,
      });
  }

  sparkles(x: number, y: number, size: number): void {
    for (let i = 0; i < 8; i++)
      this.particles.push({
        x: x + randomBetween(-0.4, 0.4) * size,
        y: y + randomBetween(-0.38, 0.3) * size,
        vx: randomBetween(-12, 12),
        vy: -32,
        age: 0,
        life: 0.7,
        radius: randomBetween(2, 4),
        rotation: randomBetween(0, Math.PI),
        kind: 'sparkle',
      });
  }

  update(dt: number): void {
    for (const prop of this.props) prop.age += dt;
    this.props = this.props.filter((prop) => prop.age < prop.life);
    for (const log of this.logs) log.age += dt;
    this.logs = this.logs.filter((log) => log.age < log.life);
    for (const ghost of this.afterimages) ghost.age += dt;
    this.afterimages = this.afterimages.filter((ghost) => ghost.age < ghost.life);
    for (const particle of this.particles) {
      particle.age += dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vx *= Math.exp(-dt * 2);
      particle.vy += (particle.kind === 'smoke' ? -20 : 90) * dt;
    }
    this.particles = this.particles.filter((p) => p.age < p.life);
    for (const ring of this.rings) ring.age += dt;
    this.rings = this.rings.filter((r) => r.age < r.life);
    const completed: Projectile[] = [];
    for (const projectile of this.projectiles) {
      projectile.age += dt;
      if (projectile.age >= projectile.duration) completed.push(projectile);
    }
    this.projectiles = this.projectiles.filter((p) => p.age < p.duration);
    for (const projectile of completed) {
      this.burst(projectile.to.x, projectile.to.y);
      projectile.impact();
    }
    for (const summon of this.summons) {
      summon.age += dt;
      if (summon.age >= summon.life) this.smoke(summon.x, summon.y, 1.2);
    }
    this.summons = this.summons.filter((s) => s.age < s.life);
    if (this.charge) this.charge.time += dt;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const particle of this.particles) {
      const t = particle.age / particle.life;
      ctx.save();
      ctx.globalAlpha = (1 - t) * (particle.kind === 'smoke' ? 0.85 : 1);
      ctx.translate(particle.x, particle.y);
      if (particle.kind === 'smoke') {
        const radius = particle.radius * (0.55 + t * 1.25);
        ctx.fillStyle = '#f4f0ed';
        ctx.strokeStyle = '#a69b9d';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        for (let i = 0; i <= 32; i++) {
          const a = (i / 32) * Math.PI * 2;
          const r = radius * (1 + 0.16 * Math.cos(a * 5 + particle.rotation));
          if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
          else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      } else if (particle.kind === 'sparkle') {
        ctx.rotate(particle.rotation + t);
        ctx.fillStyle = '#efb94e';
        ctx.beginPath();
        const radius = particle.radius * (0.6 + Math.sin(t * Math.PI));
        for (let i = 0; i < 8; i++) {
          const angle = (i * Math.PI) / 4;
          const r = i % 2 ? radius * 0.3 : radius;
          if (i === 0) ctx.moveTo(Math.cos(angle) * r, Math.sin(angle) * r);
          else ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
        }
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.rotate(particle.rotation);
        ctx.fillStyle =
          particle.kind === 'crumb'
            ? '#efc27f'
            : particle.kind === 'feather'
              ? '#5c556d'
              : '#70ddff';
        ctx.fillRect(
          -particle.radius * 3,
          -particle.radius / 2,
          particle.radius * 6,
          particle.radius,
        );
      }
      ctx.restore();
    }
    for (const ring of this.rings) {
      const t = ring.age / ring.life;
      ctx.strokeStyle = `${ring.color}${(1 - t) * 0.8})`;
      ctx.lineWidth = 3 * (1 - t);
      ctx.beginPath();
      ctx.arc(ring.x, ring.y, ring.radius * Math.sqrt(t), 0, Math.PI * 2);
      ctx.stroke();
    }
    for (const projectile of this.projectiles) {
      const t = projectile.age / projectile.duration;
      for (let i = 4; i >= 0; i--) {
        const p = arcPosition(projectile.from, projectile.to, Math.max(0, t - i * 0.025), 55);
        ctx.globalAlpha = i === 0 ? 1 : 0.1 * (1 - i / 5);
        this.drawProjectile?.(
          ctx,
          p.x,
          p.y,
          lerp(projectile.radius, 34, t) - i * 2,
          projectile.rotation + projectile.age * 22,
        );
      }
      ctx.globalAlpha = 1;
    }
    if (this.charge)
      this.drawProjectile?.(
        ctx,
        this.charge.x,
        this.charge.y,
        this.charge.radius,
        this.charge.time * 22,
        this.charge.formation,
      );
  }

  clear(): void {
    this.particles = [];
    this.projectiles = [];
    this.rings = [];
    this.summons = [];
    this.logs = [];
    this.props = [];
    this.afterimages = [];
    this.charge = undefined;
  }
}
